(() => {
  const initialized = new WeakMap();

  class HCRelatedSwatches {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-hc-related-track]');
      this.viewport = root.querySelector('[data-hc-related-viewport]');
      this.swatches = Array.from(root.querySelectorAll('[data-hc-related-swatch]'));
      this.prevButton = root.querySelector('[data-hc-related-prev]');
      this.nextButton = root.querySelector('[data-hc-related-next]');
      this.card = root.closest('product-card');
      this.productImage = this.card?.querySelector('[data-product-card-image]');
      this.swatchImage = this.card?.querySelector('[data-product-card-swatch-image]');
      this.defaultSwatch = root.querySelector('[data-hc-related-default]') || this.swatches[0];
      this.currentIndex = 0;
      this.visibleCount = 1;

      if (!this.track || !this.viewport || this.swatches.length === 0) return;

      this.handleResize = this.handleResize.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);

      this.setupCardImage();
      this.setActiveSwatch(this.defaultSwatch);
      this.attachEvents();
      this.handleResize();
    }

    attachEvents() {
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => {
          this.setActiveSwatch(swatch);
        });
      });

      this.root.addEventListener('mouseleave', this.handleMouseLeave);
      this.root.addEventListener('focusin', this.handleFocusIn);
      this.root.addEventListener('focusout', this.handleFocusOut);
      window.addEventListener('resize', this.handleResize);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }
    }

    setupCardImage() {
      if (!this.swatchImage) return;

      this.swatchImage.removeAttribute('loading');
      this.swatchImage.removeAttribute('srcset');
    }

    getMaxIndex() {
      return Math.max(0, this.swatches.length - this.visibleCount);
    }

    getGapSize() {
      const trackStyles = window.getComputedStyle(this.track);
      return parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
    }

    getStepSize() {
      const firstSwatch = this.swatches[0];
      if (!firstSwatch) return 0;

      return firstSwatch.getBoundingClientRect().width + this.getGapSize();
    }

    getVisibleCount() {
      const step = this.getStepSize();
      const viewportWidth = this.viewport.getBoundingClientRect().width;

      if (!step || !viewportWidth) return 1;

      return Math.max(1, Math.floor((viewportWidth + this.getGapSize()) / step));
    }

    setTrackPosition() {
      const step = this.getStepSize();
      this.track.style.transform = `translate3d(${-this.currentIndex * step}px, 0, 0)`;
    }

    handleResize() {
      this.visibleCount = this.getVisibleCount();
      this.currentIndex = Math.min(this.currentIndex, this.getMaxIndex());
      this.setTrackPosition();
      this.updateButtons();
    }

    handlePrevClick() {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.setTrackPosition();
      this.updateButtons();
    }

    handleNextClick() {
      this.currentIndex = Math.min(this.getMaxIndex(), this.currentIndex + 1);
      this.setTrackPosition();
      this.updateButtons();
    }

    handleMouseLeave() {
      if (!this.root.matches(':focus-within')) {
        this.restoreDefaultState();
      }
    }

    handleFocusIn(event) {
      const swatch = event.target.closest('[data-hc-related-swatch]');
      if (swatch) {
        this.setActiveSwatch(swatch);
      }
    }

    handleFocusOut(event) {
      if (this.root.contains(event.relatedTarget)) return;
      this.restoreDefaultState();
    }

    restoreDefaultState() {
      this.setActiveSwatch(this.defaultSwatch);
    }

    setActiveSwatch(activeSwatch) {
      if (!activeSwatch) return;

      this.swatches.forEach((swatch) => {
        swatch.classList.toggle('is-active', swatch === activeSwatch);
      });

      this.updateCardImage(activeSwatch);
    }

    updateCardImage(activeSwatch) {
      if (!this.productImage || !this.swatchImage) return;

      if (!activeSwatch.hasAttribute('data-hc-related-default') && activeSwatch.dataset.hcRelatedImageSrc) {
        this.swatchImage.setAttribute('src', activeSwatch.dataset.hcRelatedImageSrc);
        this.swatchImage.setAttribute('alt', activeSwatch.dataset.hcRelatedImageAlt || '');
        this.swatchImage.style.opacity = '1';
        this.productImage.style.opacity = '0';
        return;
      }

      this.swatchImage.style.opacity = '0';
      this.productImage.style.opacity = '1';
    }

    updateButtons() {
      const hasOverflow = this.swatches.length > this.visibleCount;

      if (this.prevButton) {
        this.prevButton.hidden = !hasOverflow;
        this.prevButton.disabled = !hasOverflow || this.currentIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !hasOverflow;
        this.nextButton.disabled = !hasOverflow || this.currentIndex >= this.getMaxIndex();
      }
    }
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-hc-related-swatches]').forEach((element) => {
      if (!initialized.has(element)) {
        initialized.set(element, new HCRelatedSwatches(element));
      } else {
        initialized.get(element).handleResize();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
